import axios from 'axios';
import auth from '../Auth/Auth';  // Import the auth utility

import BASE_URL from '../config' ;

// API base URL
const API_BASE_URL = BASE_URL;

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = auth.getToken();
  if (!token) return {};
  
  return {
    'Authorization': `Bearer ${token}`
  };
};

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_BASE_URL
});

// Add request interceptor to attach authentication token to all requests
axiosInstance.interceptors.request.use(
  (config) => {
    // Add authorization header with JWT token if available
    const token = auth.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Transaction API endpoints
const API = {
  // Get all transactions
  getTransactions: async () => {
    try {
      const response = await axiosInstance.get('/transactions');
      return response.data;
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  },

  // Get a single transaction by ID
  getTransaction: async (transactionId) => {
    try {
      const response = await axiosInstance.get(`/transactions/${transactionId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching transaction ${transactionId}:`, error);
      throw error;
    }
  },

  // Confirm a transaction (change status to COMPLETED)
  confirmTransaction: async (transactionId) => {
    try {
      const response = await axiosInstance.post(`/transactions/confirm`, {
        transaction_id: transactionId
      });
      return response.data;
    } catch (error) {
      console.error(`Error confirming transaction ${transactionId}:`, error);
      throw error;
    }
  },

  // Update transaction details
  updateTransaction: async (transactionId, data) => {
    try {
      const response = await axiosInstance.put(`/transactions/update`, {
        transaction_id: transactionId,
        ...data
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating transaction ${transactionId}:`, error);
      throw error;
    }
  },

  // Generate receipt for a transaction
  generateReceipt: async (transactionId) => {
    try {
      const response = await axiosInstance.get(`/transactions/${transactionId}/receipt`);
      return response.data;
    } catch (error) {
      console.error(`Error generating receipt for transaction ${transactionId}:`, error);
      throw error;
    }
  },

  // Search transactions by various criteria
  searchTransactions: async (searchParams) => {
    try {
      const response = await axiosInstance.get(`/transactions/search`, {
        params: searchParams
      });
      return response.data;
    } catch (error) {
      console.error('Error searching transactions:', error);
      throw error;
    }
  }
};

// Helper function for API requests with authorization headers
const authFetch = async (url, options = {}) => {
  const headers = {
    ...options.headers,
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...getAuthHeaders()
  };

  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'API request failed');
  }
  return data;
};

export const fetchApartments = async () => {
  try {
    const data = await authFetch('/apartments');
    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch apartments');
    }
    return data.apartments;
  } catch (error) {
    console.error('Error fetching apartments:', error);
    throw error;
  }
};

export const createApartment = async (apartmentData) => {
  try {
    const data = await authFetch('/apartments', {
      method: 'POST',
      body: JSON.stringify(apartmentData)
    });
    if (!data.success) {
      throw new Error(data.error || 'Failed to create apartment');
    }
    return data.apartment;
  } catch (error) {
    console.error('Error creating apartment:', error);
    throw error;
  }
};

export const fetchCategories = async (apartmentId) => {
  try {
    const data = await authFetch(`/apartments/${apartmentId}/categories`);
    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch categories');
    }
    return data.categories;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

export const createCategory = async (apartmentId, categoryData) => {
  try {
    const data = await authFetch(`/apartments/${apartmentId}/categories`, {
      method: 'POST',
      body: JSON.stringify(categoryData)
    });
    if (!data.success) {
      throw new Error(data.error || 'Failed to create category');
    }
    return data.category;
  } catch (error) {
    console.error('Error creating category:', error);
    throw error;
  }
};

export const fetchUnits = async (categoryId) => {
  try {
    const data = await authFetch(`/categories/${categoryId}/units`);
    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch units');
    }
    return data.units;
  } catch (error) {
    console.error('Error fetching units:', error);
    throw error;
  }
};

export const createUnit = async (categoryId, unitData) => {
  try {
    const data = await authFetch(`/categories/${categoryId}/units`, {
      method: 'POST',
      body: JSON.stringify(unitData)
    });
    if (!data.success) {
      throw new Error(data.error || 'Failed to create unit');
    }
    return data.unit;
  } catch (error) {
    console.error('Error creating unit:', error);
    throw error;
  }
};

export const updateApartment = async (apartmentId, apartmentData) => {
  try {
    const data = await authFetch(`/apartments/${apartmentId}`, {
      method: 'PUT',
      body: JSON.stringify(apartmentData)
    });
    if (!data.success) {
      throw new Error(data.error || 'Failed to update apartment');
    }
    return data.apartment;
  } catch (error) {
    console.error('Error updating apartment:', error);
    throw error;
  }
};

export const deleteApartment = async (apartmentId) => {
  try {
    const data = await authFetch(`/apartments/${apartmentId}`, {
      method: 'DELETE'
    });
    if (!data.success) {
      throw new Error(data.error || 'Failed to delete apartment');
    }
    return data;
  } catch (error) {
    console.error('Error deleting apartment:', error);
    throw error;
  }
};

// Update and delete functions for categories
export const updateCategory = async (categoryId, categoryData) => {
  try {
    const data = await authFetch(`/categories/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData)
    });

    if (!data.success) {
      throw new Error(data.error || 'Failed to update category');
    }

    return data.category;
  } catch (error) {
    console.error('Error updating category:', error);
    throw error;
  }
};

export const deleteCategory = async (categoryId) => {
  try {
    const data = await authFetch(`/categories/${categoryId}`, {
      method: 'DELETE'
    });
    if (!data.success) {
      throw new Error(data.error || 'Failed to delete category');
    }
    return data;
  } catch (error) {
    console.error('Error deleting category:', error);
    throw error;
  }
};

// Update and delete functions for units
export const deleteUnit = async (unitId) => {
  try {
    const data = await authFetch(`/units/${unitId}`, {
      method: 'DELETE'
    });
    if (!data.success) {
      throw new Error(data.error || 'Failed to delete unit');
    }
    return data;
  } catch (error) {
    console.error('Error deleting unit:', error);
    throw error;
  }
};

export const updateUnit = async (unitId, unitData) => {
  try {
    const data = await authFetch(`/units/${unitId}`, {
      method: 'PUT',
      body: JSON.stringify(unitData)
    });

    if (!data.success) {
      throw new Error(data.error || 'Failed to update unit');
    }

    return data.unit;
  } catch (error) {
    console.error(`Error updating unit ${unitId}:`, error);
    throw error;
  }
};

// ==================== IMAGE MANAGEMENT API ====================

// Fetch apartment images
export const fetchApartmentImages = async (apartmentId) => {
  try {
    const data = await authFetch(`/apartments/${apartmentId}/images`);
    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch images');
    }
    return data.images;
  } catch (error) {
    console.error('Error fetching apartment images:', error);
    throw error;
  }
};

// Prepare upload (get presigned URL)
// mediaType/fileSize/durationSeconds are optional — omit them (or pass nothing)
// for the original image-only behavior. Existing callers that only pass
// (apartmentId, filename, contentType, categoryId) are unaffected.
export const prepareImageUpload = async (
  apartmentId,
  filename,
  contentType,
  categoryId = null,
  { mediaType = 'image', fileSize = null, durationSeconds = null } = {}
) => {
  try {
    const data = await authFetch('/images/prepare-upload', {
      method: 'POST',
      body: JSON.stringify({
        apartment_id: apartmentId,
        category_id: categoryId,
        filename: filename,
        content_type: contentType,
        media_type: mediaType,
        file_size: fileSize,
        duration_seconds: durationSeconds
      })
    });
    
    if (!data.upload_url) {
      throw new Error('Failed to prepare upload');
    }
    
    return data; // { upload_url, public_url, image_id, media_type }
  } catch (error) {
    console.error('Error preparing upload:', error);
    throw error;
  }
};

// Upload file directly to R2
export const uploadToR2 = async (uploadUrl, file) => {
  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type
      },
      body: file
    });
    
    if (!response.ok) {
      throw new Error('Failed to upload to R2');
    }
    
    return true;
  } catch (error) {
    console.error('Error uploading to R2:', error);
    throw error;
  }
};

// Confirm upload after the file has landed in R2 — required for videos.
// The backend re-checks duration now that the file actually exists and will
// delete the record + R2 object if it's over the limit, in which case this
// throws and the image_id is no longer valid.
export const confirmMediaUpload = async (imageId, { durationSeconds = null, fileSize = null, thumbnailUrl = null } = {}) => {
  try {
    const data = await authFetch(`/images/${imageId}/confirm-upload`, {
      method: 'POST',
      body: JSON.stringify({
        duration_seconds: durationSeconds,
        file_size: fileSize,
        thumbnail_url: thumbnailUrl
      })
    });

    if (!data.success || !data.image) {
      throw new Error(data.error || 'Failed to confirm upload');
    }

    return data.image;
  } catch (error) {
    console.error('Error confirming upload:', error);
    throw error;
  }
};

// Update image (set cover, change order, etc)
export const updateImage = async (imageId, updates) => {
  try {
    const data = await authFetch(`/images/${imageId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
    
    // ✅ Match the actual response shape
    if (!data.success || !data.image) {
      throw new Error(data.error || 'Failed to update image');
    }
    
    return data.image; // Return the image object, not the wrapper
  } catch (error) {
    console.error('Error updating image:', error);
    throw error;
  }
};

// Delete image
export const deleteImage = async (imageId) => {
  try {
    await authFetch(`/images/${imageId}`, {
      method: 'DELETE'
    });
    return true;
  } catch (error) {
    console.error('Error deleting image:', error);
    throw error;
  }
};

// Fetch category images
export const fetchCategoryImages = async (categoryId) => {
  try {
    const data = await authFetch(`/categories/${categoryId}/images`);
    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch category images');
    }
    return data.images;
  } catch (error) {
    console.error('Error fetching category images:', error);
    throw error;
  }
};
/**
 * Fetch all units for an apartment (flat list, includes groupId / groupName).
 * Used by the "All Units" overview page.
 */
export const fetchApartmentUnits = async (apartmentId) => {
  try {
    const data = await authFetch(`/apartments/${apartmentId}/units`);
    if (!data.success) throw new Error(data.error || 'Failed to fetch units');
    return data.units;
  } catch (error) {
    console.error('Error fetching apartment units:', error);
    throw error;
  }
};
 
/**
 * Fetch all groups (with their ordered units) for a category.
 */
export const fetchUnitGroups = async (categoryId) => {
  try {
    const data = await authFetch(`/categories/${categoryId}/groups`);
    if (!data.success) throw new Error(data.error || 'Failed to fetch groups');
    return data.groups;
  } catch (error) {
    console.error('Error fetching unit groups:', error);
    throw error;
  }
};
 
/**
 * Atomically replace all groups for a category.
 *
 * @param {number} categoryId
 * @param {Array<{
 *   id?: number,          // existing group id (omit for new)
 *   name: string,
 *   order: number,
 *   unitIds: number[],    // unit ids in desired order
 * }>} groups
 */
export const saveUnitGroups = async (categoryId, groups) => {
  try {
    const data = await authFetch(`/categories/${categoryId}/groups/save`, {
      method: 'POST',
      body: JSON.stringify({ groups }),
    });
    if (!data.success) throw new Error(data.error || 'Failed to save groups');
    return data.groups;
  } catch (error) {
    console.error('Error saving unit groups:', error);
    throw error;
  }
};
 
/**
 * Delete a single group (units become ungrouped; nothing else is affected).
 */
export const deleteUnitGroup = async (groupId) => {
  try {
    const data = await authFetch(`/groups/${groupId}`, { method: 'DELETE' });
    if (!data.success) throw new Error(data.error || 'Failed to delete group');
    return data;
  } catch (error) {
    console.error('Error deleting unit group:', error);
    throw error;
  }
};

export default API;